import capitalize from 'utils/capitalise';
import type { AssessmentDisplayName } from 'containers/ProjectView/types';
import { APP_TYPES } from './constants';

// Group user can create, edit own, view all, and reassign
export const CREATE_EDIT_VIEW_AS_A_GROUP_CODE = '11101';

export const NULL_PERMISSION = '00000';

export enum EntityType {
  GROUP = 'group',
  GROUP_USER = 'group_user',
  USER = 'user',
}

export enum RoleType {
  OWNER = 'owner',
  MEMBER = 'member',
}

const bitToBool = (bitString: string) => bitString === '1';
const boolToBit = (bool = false) => (bool ? '1' : '0');

export interface Permission {
  create: boolean;
  editOwn: boolean;
  viewAll: boolean;
  delete: boolean;
  reassign: boolean;
  /**
   * this indicates that user has CRU all permission, and is also in a sharing group.
   * This is needed because entityType would not be group in this case. If the user
   * returned permission is group_user type, this might not be set
   * */
  hasGroupUserSharing?: boolean;
  entityType: EntityType | null;
}

export const processPermission = (
  permissionString = '',
  entityType: EntityType | null,
  hasGroupUserSharing = false
): Permission => ({
  create: bitToBool(permissionString.charAt(0)),
  editOwn: bitToBool(permissionString.charAt(1)),
  viewAll: bitToBool(permissionString.charAt(2)),
  delete: bitToBool(permissionString.charAt(3)),
  reassign: bitToBool(permissionString.charAt(4)),
  hasGroupUserSharing,
  entityType,
});

export const getNullPermission = () => processPermission(NULL_PERMISSION, null);

export const permissionToBits = (permission: Partial<Permission>) =>
  `${boolToBit(permission.create)}${boolToBit(permission.editOwn)}${boolToBit(
    permission.viewAll
  )}${boolToBit(permission.delete)}${boolToBit(permission.reassign)}`;

export const flowPermToString = (
  permission: Permission,
  role: RoleType,
  terms: AssessmentDisplayName
) => {
  if (role === RoleType.OWNER) {
    return 'All actions allowed';
  }

  const permissionString = [
    permission.editOwn && `Edit own ${terms.assessmentTermPlural}`,
    permission.create && `Create ${terms.assessmentTermPlural}`,
    permission.viewAll && `View all ${terms.assessmentTermPlural}`,
    permission.reassign &&
      `Reassign ${terms.assessmentTermPlural} & Send reminders`,
    permission.delete && `Delete own ${terms.assessmentTermPlural}`,
  ];

  return permissionString.filter(Boolean).join(', ');
};

export const dataPermToString = (permission: Partial<Permission> = {}) =>
  Object.keys(permission)
    .reduce<string[]>((acc, perm: string) => {
      if (permission[perm as keyof Permission]) {
        acc.push(capitalize(perm));
      }

      return acc;
    }, [])
    .join(', ');

export const permissionToString = (
  appType: string,
  permission: Permission,
  role: RoleType,
  terms: AssessmentDisplayName
) => {
  switch (appType) {
    case APP_TYPES.FLOW:
      return flowPermToString(permission, role, terms);
    case APP_TYPES.DATA:
      return dataPermToString(permission);
    default:
      return 'Unknown';
  }
};
